import WizardProgressBar from '@/components/shared/WizardProgressBar';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import OrderSummarySidebar from '@/components/wizard/OrderSummarySidebar';
import ReviewEntitiesTable from '@/components/wizard/ReviewEntitiesTable';
import { saveWizardProgress } from '@/lib/dataService';
import { buildQuoteRequest, summariseQuote } from '@/lib/orderQuote';
import { formatRegionMoneyMinor, getActiveRegionConfig } from '@/lib/regionConfig';
import { useOrderQuote } from '@/lib/useOrderQuote';
import { withTaxComputation } from '@/lib/taxComputation';
import { jobsApi, paymentsApi } from '@/services/api';
import { OWN_IXBRL_PRICING_TYPE, ordersOwnIxbrl, withOwnIxbrl } from '@/lib/ownIxbrl';
import { ArrowLeft, ArrowRight, CreditCard, Loader2, Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const Step3Review = () => {
  const navigate = useNavigate();
  const [wizardData, setWizardData] = useState(null);
  const [paymentType, setPaymentType] = useState('pay_now');
  const [isRedirecting, setIsRedirecting] = useState(false);
  const regionConfig = getActiveRegionConfig();

  useEffect(() => {
    const data = JSON.parse(sessionStorage.getItem('ixbrl_wizard') || '{}');
    if (!data.selectedCompanies || data.selectedCompanies.length === 0) {
      toast.error('Session expired. Please start again.');
      navigate('/wizard/start');
      return;
    }
    setWizardData(data);

    // Handle Stripe cancel redirect
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment') === 'cancelled') {
      toast.info('Payment was cancelled. You can try again or choose Pay Later.');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [navigate]);

  const handleSaveProgress = async () => {
    const currentData = JSON.parse(sessionStorage.getItem('ixbrl_wizard') || '{}');
    currentData.paymentType = paymentType;
    currentData.savedAt = new Date().toISOString();
    await saveWizardProgress(currentData);
    toast.success('Progress saved!');
  };

  const handleConfirm = async () => {
    // The draft order's key, made once and saved with the draft. The server answers the same key with the same jobs,
    // so a double click, a cancelled Stripe payment or coming back days later never creates a second order.
    const orderKey = wizardData.orderKey || crypto.randomUUID();
    const updatedData = {...wizardData, orderKey, paymentType, step: 3, savedAt: new Date().toISOString()};
    sessionStorage.setItem('ixbrl_wizard', JSON.stringify(updatedData));
    try { await saveWizardProgress(updatedData); } catch (e) { /* non-blocking */ }

    setIsRedirecting(true);

    // Jobs (and their invoices) are created here, before file upload, so
    // an invoice exists as soon as the order is confirmed for every
    // payment method -- not just when a file is later attached in Step 4.
    const optionalServices = Array.isArray(wizardData.optionalServices)
      ? wizardData.optionalServices
      : Object.keys(wizardData.optionalServices || {}).filter(k => wizardData.optionalServices[k]);
    const selectedServices = [...optionalServices];
    const hasTaggedAccounts = wizardData.selectedCompanies.some(company => Boolean(company.accountType));
    if (hasTaggedAccounts && regionConfig.primaryService && !selectedServices.includes(regionConfig.primaryService)) {
      selectedServices.unshift(regionConfig.primaryService);
    }

    const jobItems = wizardData.selectedCompanies.map(company => ({
      company_id: company.id,
      company_name: company.name,
      entity_name: company.entityName || company.name,
      company_number: company.registrationNumber || null,
      uen: (wizardData.region || regionConfig.code) === 'SG' ? company.registrationNumber || null : null,
      services: selectedServices,
      account_type: company.accountType,
      financial_year: wizardData.config?.financialYear,
      service_type: wizardData.config?.serviceType || 'expert',
      // Restored wizard sessions can predate the delivery-time field even
      // when the review card renders its standard label. Keep the API
      // contract explicit instead of sending undefined (FastAPI then reports
      // only the unhelpful generic "Field required" message).
      delivery_time: wizardData.config?.deliveryTime || 'standard',
      payment_type: paymentType,
      region_code: wizardData.region || regionConfig.code,
      source_site: wizardData.sourceSite || regionConfig.sourceSite,
    }));

    let createdJobs;
    try {
      const result = await jobsApi.createJobs({ jobs: jobItems, order_key: orderKey });
      createdJobs = result.jobs || [];
    } catch (err) {
      setIsRedirecting(false);
      toast.error(err.message || 'Failed to create order. Please try again.');
      return;
    }

    const dataWithJobs = { ...updatedData, createdJobs };
    sessionStorage.setItem('ixbrl_wizard', JSON.stringify(dataWithJobs));
    setWizardData(dataWithJobs); // keep local state in sync so a retry within the same page load also reuses these jobs
    try { await saveWizardProgress(dataWithJobs); } catch (e) { /* non-blocking */ }

    if (paymentType === 'pay_now') {
      try {
        // Convert selected companies to entity_prices dict: {company_id: account_type}
        const entity_prices = {};
        wizardData.selectedCompanies.forEach(company => {
          entity_prices[company.id] = company.accountType || (ordersOwnIxbrl(wizardData) ? OWN_IXBRL_PRICING_TYPE : 'standard');
        });

        const result = await paymentsApi.startCheckout({
          entity_prices,
          optional_services: selectedServices,
          express: wizardData.config?.deliveryTime === 'express',
          region_code: wizardData.region || regionConfig.code,
          company_ids: wizardData.selectedCompanies.map(c => c.id),
          job_ids: createdJobs.map(j => j.id),
        });
        window.location.href = result.url;
      } catch (err) {
        if (err.status === 409) {
          // The server says these jobs are already paid: never let the client retry.
          toast.info(err.responseDetail || 'This order has already been paid.');
          navigate('/jobs');
          return;
        }
        setIsRedirecting(false);
        toast.error(err.message || 'Failed to start payment. Please try again.');
      }
    } else {
      setIsRedirecting(false);
      navigate('/wizard/upload');
    }
  };

  // The price comes from the back end, which prices with the same code as the invoice and the payment.
  const { quote, loading: quoteLoading, error: quoteError } = useOrderQuote(buildQuoteRequest(wizardData));

  if (!wizardData) return null;

  const accountTypeByValue = Object.fromEntries(regionConfig.accountTypes.map((type) => [type.value, type]));
  const optionalServiceById = withOwnIxbrl(withTaxComputation({
    ...regionConfig.serviceCatalog,
    ...Object.fromEntries(regionConfig.optionalServices.map((service) => [service.id, service])),
  }, regionConfig.code), regionConfig.code);

  // Step1 saves the ordered services as an array; support the legacy object format too
  const optionalServices = Array.isArray(wizardData.optionalServices)
    ? wizardData.optionalServices
    : Object.keys(wizardData.optionalServices || {}).filter(k => wizardData.optionalServices[k]);
  const summary = summariseQuote(quote);
  const quoteByCompany = Object.fromEntries((quote?.companies || []).map((company) => [company.company_id, company]));

  const accountTypeNames = Object.fromEntries(regionConfig.accountTypes.map((type) => [type.value, type.label]));
  const optionalServiceNames = Object.fromEntries(
    Object.entries(optionalServiceById).map(([id, service]) => [id, service.title || id]),
  );

  const selectedServiceNames = optionalServices
    .map((service) => optionalServiceNames[service])
    .filter(Boolean);

  return (
    <div data-testid="step3-review">
      <div className="page-header">
        <h1 className="page-title">Create New Job</h1>
        <p className="page-subtitle">Step 3 of 4: Review and confirm</p>
      </div>

      <div className="flex gap-6">
        {/* Main Content */}
        <div className="flex-1">
          {/* Companies */}
          <ReviewEntitiesTable
            companies={wizardData.selectedCompanies}
            regionConfig={regionConfig}
            accountTypeNames={accountTypeNames}
            serviceNames={selectedServiceNames}
            financialYear={wizardData.config?.financialYear}
            deliveryTime={wizardData.config?.deliveryTime}
            quoteByCompany={quoteByCompany}
            quoteLoading={quoteLoading}
          />

          {/* Payment Method */}
          <div className="bg-card border border-border rounded-lg p-4 mb-4">
            <h3 className="font-semibold mb-3 flex items-center gap-2 text-sm">
              <CreditCard className="w-4 h-4" />
              Payment Method
            </h3>
            <RadioGroup value={paymentType} onValueChange={setPaymentType} className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className={`flex items-center space-x-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                paymentType === 'pay_now' ? 'border-primary bg-primary/5' : 'border-border hover:bg-accent'
              }`}>
                <RadioGroupItem value="pay_now" id="pay_now" />
                <Label htmlFor="pay_now" className="cursor-pointer flex-1">
                  <p className="font-medium text-sm">Pay Now</p>
                  <p className="text-xs text-muted-foreground">Credit card or bank transfer</p>
                </Label>
              </div>
              <div className={`flex items-center space-x-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                paymentType === 'invoice_later' ? 'border-primary bg-primary/5' : 'border-border hover:bg-accent'
              }`}>
                <RadioGroupItem value="invoice_later" id="invoice_later" />
                <Label htmlFor="invoice_later" className="cursor-pointer flex-1">
                  <p className="font-medium text-sm">Pay Later</p>
                  <p className="text-xs text-muted-foreground">Payment due before delivery</p>
                </Label>
              </div>
              <div className={`flex items-center space-x-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                paymentType === 'credit' ? 'border-primary bg-primary/5' : 'border-border hover:bg-accent'
              }`}>
                <RadioGroupItem value="credit" id="credit" />
                <Label htmlFor="credit" className="cursor-pointer flex-1">
                  <p className="font-medium text-sm">Request Credit</p>
                  <p className="text-xs text-muted-foreground">30-day terms — for credit account holders (5+ files)</p>
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Total Cost */}
          <div className="bg-primary text-primary-foreground rounded-lg p-4 mb-4" data-testid="review-total">
            {quote && summary.discount > 0 && (
              <div className="text-sm opacity-90 mb-2 space-y-0.5">
                <div className="flex justify-between"><span>Subtotal</span><span>{formatRegionMoneyMinor(summary.subtotal, regionConfig)}</span></div>
                <div className="flex justify-between"><span>Volume discount ({summary.discountPercent}%)</span><span>-{formatRegionMoneyMinor(summary.discount, regionConfig)}</span></div>
              </div>
            )}
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm opacity-90">Total Amount</p>
                <p className="text-xs opacity-75">{wizardData.selectedCompanies.length} {wizardData.selectedCompanies.length === 1 ? 'entity' : 'entities'}</p>
              </div>
              <span className="text-2xl font-bold">{quote ? formatRegionMoneyMinor(summary.total, regionConfig) : '...'}</span>
            </div>
            {quoteError && <p className="text-xs mt-2" data-testid="review-price-error">Could not work out the price. {quoteError}</p>}
          </div>

          <div className="flex justify-between">
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => navigate('/wizard/companies')}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button variant="ghost" onClick={handleSaveProgress}>
                <Save className="w-4 h-4 mr-2" />
                Save
              </Button>
            </div>
            <Button onClick={handleConfirm} disabled={isRedirecting} data-testid="confirm-btn">
              {isRedirecting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  {paymentType === 'pay_now' ? 'Proceed to Payment' : 'Confirm Order'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Order Summary Sidebar - Top Aligned */}
        <div className="w-72 flex-shrink-0">
          <WizardProgressBar currentStep={3} />
          <OrderSummarySidebar wizardData={{...wizardData, paymentType}} currentStep={3} />
        </div>
      </div>
    </div>
  );
};

export default Step3Review;
